import React, { useState } from 'react';
import { X, Loader2, Send, Copy, CheckCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

interface MessageDraft {
  id: string;
  subject: string;
  body: string;
  recommended_tone?: string;
  reason?: string;
}

interface MessageDraftModalProps {
  draft: MessageDraft;
  leadName: string;
  onClose: () => void;
  onRegenerate: () => void;
}

export default function MessageDraftModal({ draft, leadName, onClose, onRegenerate }: MessageDraftModalProps) {
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = `נושא: ${subject}\n\n${body}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('הועתק ללוח');
    setTimeout(() => setCopied(false), 2000);
  };

  const messageType = draft.recommended_tone || '';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" dir="rtl">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-[640px] mx-4 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <span>✉️</span> טיוטת הודעה
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">עבור: {leadName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Type badge */}
          {messageType && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-900/50 text-blue-300 border border-blue-700/50">
                {messageType}
              </span>
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">נושא:</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">תוכן ההודעה:</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={10}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 resize-y leading-relaxed"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-700">
          <p className="text-[11px] text-gray-600">טיוטה בלבד — לא נשלחה</p>
          <div className="flex items-center gap-2">
            <button
              onClick={onRegenerate}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
            >
              <RefreshCw size={13} />
              ייצר מחדש
            </button>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
            >
              {copied ? <CheckCircle size={13} /> : <Copy size={13} />}
              {copied ? 'הועתק' : 'העתק'}
            </button>
            <button
              disabled
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600/50 text-blue-200 cursor-not-allowed"
              title="שליחה תתאפשר בקרוב"
            >
              <Send size={13} />
              שלח (בקרוב)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface GeneratingModalProps {
  leadName: string;
}

export function GeneratingModal({ leadName }: GeneratingModalProps) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" dir="rtl">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl p-8 text-center max-w-sm mx-4">
        <Loader2 size={32} className="animate-spin text-blue-400 mx-auto mb-3" />
        <p className="text-white font-medium">מייצר טיוטת הודעה...</p>
        <p className="text-xs text-gray-400 mt-1">עבור: {leadName}</p>
      </div>
    </div>
  );
}
